import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Add, Visibility, Download, VerifiedUser } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fetchEmployeeDocuments, updateEmployeeDocument } from '../../../services/employee';
import CustomPagination from '../../../components/common/CustomPagination.js';

const Documents = () => {
  const { t } = useTranslation(['common', 'employees']);
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [filters, setFilters] = useState({
    employee: '',
    document_type: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  // Use same protocol as current page to avoid "insecure download blocked" (mixed content: HTTPS page + HTTP link)
  const secureFileUrl = (url) => {
    if (!url || typeof url !== 'string') return url;
    if (window.location.protocol === 'https:' && url.startsWith('http://')) {
      return 'https:' + url.slice(5);
    }
    return url;
  };

  const handleViewDocument = (fileUrl) => {
    const url = secureFileUrl(fileUrl);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadDocument = (fileUrl, title) => {
    const url = secureFileUrl(fileUrl);
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', title || 'document');
    link.setAttribute('target', '_blank');
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleToggleVerified = async (doc) => {
    if (!doc?.id) return;
    setVerifyingId(doc.id);
    setError(null);
    try {
      await updateEmployeeDocument(doc.id, { is_verified: !doc.is_verified });
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to update');
    } finally {
      setVerifyingId(null);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page + 1,
        page_size: pagination.rowsPerPage
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await fetchEmployeeDocuments(params);
      
      if (response.results) {
        setDocuments(response.results);
        setPagination(prev => ({
          ...prev,
          totalCount: response.count || 0
        }));
      } else {
        setDocuments(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  };

  const handleCustomRowsInputChange = (value) => {
    setCustomRowsInput(value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput);
    if (value && value > 0 && value <= 1000) {
      setPagination(prev => ({ ...prev, rowsPerPage: value, page: 0 }));
      setCustomRowsActive(false);
      setCustomRowsInput('');
    } else {
      setCustomRowsInput('');
      setCustomRowsActive(false);
    }
  };

  const rowsWithSerial = useMemo(() => {
    return documents.map((doc, index) => ({
      ...doc,
      sn: pagination.page * pagination.rowsPerPage + index + 1
    }));
  }, [documents, pagination.page, pagination.rowsPerPage]);

  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: t('employees:documents.columns.sn'),
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'employee_name',
      headerName: t('employees:documents.columns.employee'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => params.value || 'N/A'
    },
    {
      field: 'employee_id',
      headerName: t('employees:documents.columns.employeeId'),
      flex: 1,
      minWidth: 120,
      sortable: false
    },
    {
      field: 'title',
      headerName: t('employees:documents.columns.title'),
      flex: 1,
      minWidth: 200,
      sortable: false
    },
    {
      field: 'document_type',
      headerName: t('employees:documents.columns.type'),
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const type = params.value || '';
        const typeMap = {
          'contract': t('employees:documents.types.contract'),
          'certificate': t('employees:documents.types.certificate'),
          'id_copy': t('employees:documents.types.idCopy'),
          'resume': t('employees:documents.types.resume'),
          'passport': t('employees:documents.types.passport'),
          'other': t('employees:documents.types.other')
        };
        return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ');
      }
    },
    {
      field: 'expiry_date',
      headerName: t('employees:documents.columns.expiryDate'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      field: 'is_verified',
      headerName: t('employees:documents.columns.verified'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('employees:leaveTypes.yes') : t('employees:leaveTypes.no')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'uploaded_at',
      headerName: t('employees:documents.columns.uploaded'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      field: 'actions',
      headerName: t('employees:table.actions') || 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => {
        const doc = params.row;
        const fileUrl = doc.file_url;
        const isVerifying = verifyingId === doc.id;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="View">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleViewDocument(fileUrl)}
                disabled={!fileUrl}
              >
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleDownloadDocument(fileUrl, doc.title)}
                disabled={!fileUrl}
              >
                <Download fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={doc.is_verified ? 'Unverify' : 'Mark as verified'}>
              <IconButton
                size="small"
                color={doc.is_verified ? 'success' : 'default'}
                onClick={() => handleToggleVerified(doc)}
                disabled={isVerifying}
              >
                {isVerifying ? (
                  <CircularProgress size={18} />
                ) : (
                  <VerifiedUser fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    }
  ], [t, verifyingId]);

  if (loading && documents.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          {t('employees:documents.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('common:buttons.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
            onClick={() => navigate('/admin/employees/documents/upload')}
          >
            {t('employees:documents.uploadDocument')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label={t('employees:documents.employee')}
                fullWidth
                value={filters.employee}
                onChange={(e) => handleFilterChange('employee', e.target.value)}
                placeholder={t('employees:documents.employeePlaceholder')}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label={t('employees:documents.documentType')}
                fullWidth
                value={filters.document_type}
                onChange={(e) => handleFilterChange('document_type', e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{
                  shrink: true,
                }}
              >
                <option value="">{t('employees:all')}</option>
                <option value="contract">{t('employees:documents.types.contract')}</option>
                <option value="certificate">{t('employees:documents.types.certificate')}</option>
                <option value="id_copy">{t('employees:documents.types.idCopy')}</option>
                <option value="resume">{t('employees:documents.types.resume')}</option>
                <option value="passport">{t('employees:documents.types.passport')}</option>
                <option value="other">{t('employees:documents.types.other')}</option>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minWidth: 1200,
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>
    </Box>
  );
};

export default Documents;

